import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import { FormattedRpServiceConfigurationPolicy } from "@idw-types/data/rp-service-configuration";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ToggleFormGroup } from "../../../../../common/toggle-form-group/toggle-form-group";
import { TrustScoreSliderControl } from "./trust-score-slider-control/trust-score-slider-control-container";
import { STEP_UP_PROPERTY, PASS_PROPERTY } from "@constants/policy-step-up-constants";
import { injectDefaultTrustScoreRangeValues } from "@utils/policies/trust-score-range-util";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { getAxnRpServiceOptions, getObligationStepUpToList, isObligationApiKeyCorrect } from "@utils/policies/policy-util";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type TrustScorePolicyDetailsContainerProps = {
    isDisabled: boolean;
    validationErrors: PolicyDetailErrors;
    rpServiceID: string;
    workflowID: string;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnRpServices: Array<AxnRpService>;
    policies: Array<FormattedRpServiceConfigurationPolicy>;
    updatePolicies: (policies: Array<FormattedRpServiceConfigurationPolicy>) => void;
};

export const TrustScorePolicyDetailsContainer = (props: TrustScorePolicyDetailsContainerProps) => {
    const {
        isDisabled,
        rpServiceID,
        workflowID,
        validationErrors,
        simpleRPServiceWorkflows,
        axnRpServices,
        policies,
        updatePolicies
    } = props;

    const obligationStepUpList = useMemo(() => {
        if (axnRpServices.length > 0) {
            return getObligationStepUpToList(rpServiceID, workflowID, axnRpServices, simpleRPServiceWorkflows);
        }

        return [];
    }, [axnRpServices, rpServiceID, workflowID]);

    const selectedApiKey = useMemo(() => {
        if (policies && policies.length > 0) {
            const policy = policies[0];
            if (policy.properties && policy.properties.length > 0) {
                const property = policy.properties.find(p => p.name === "stepUp.apiKey");
                if (property?.value) {
                    return property.value as string;
                }
            }
        }
        return null;
    }, [policies]);

    const axnRpServiceOptions = useMemo(() => {
        return getAxnRpServiceOptions(obligationStepUpList, selectedApiKey, axnRpServices);

    }, [obligationStepUpList, selectedApiKey]);


    const selectedStepUpApiKeyOption = useMemo(() => {
        if (selectedApiKey) {
            const option = axnRpServiceOptions.find(apiKeyOption => apiKeyOption.value === selectedApiKey);
            if (option) {
                return option;
            }
        }
        return null;
    }, [axnRpServiceOptions, selectedApiKey]);

    const shouldShowObligationWarningMessage = useMemo(() => {
        if (obligationStepUpList.length && selectedApiKey) {
            return !isObligationApiKeyCorrect(selectedApiKey, obligationStepUpList);
        }

        return false;
    }, [obligationStepUpList, selectedApiKey]);

    const handleStepUpApiKeyChange = (newValue: SelectOption) => {
        const newPolicies = (policies || []).map(policy => ({
            ...policy,
            properties: (policy.properties || []).map(property => ({
                ...property,
                value: property.name === "stepUp.apiKey" ? newValue.value : property.value
            }))
        }));
        updatePolicies(newPolicies);
    };

    const handleEnableStepUpChange = () => {
        const stepUpEnabledPreviousValue = (policies || []).some(policy => policy.stepUpEnabled);

        const newPolicies = policies.map(policy => ({
            ...policy,
            stepUpEnabled: !stepUpEnabledPreviousValue,
            properties: injectDefaultTrustScoreRangeValues(policy.properties || [], !stepUpEnabledPreviousValue)
        }));

        updatePolicies(newPolicies);
    };

    const setProperties = (values: number[]) => {
        if (policies.length > 0) {
            const properties = policies[0].properties.map(property => {
                if (isStepUpEnabled) {
                    if (property.name === STEP_UP_PROPERTY) {
                        property.value = values[0];
                    }

                    if (property.name === PASS_PROPERTY) {
                        property.value = values[1];

                    }
                } else {
                    if (property.name === PASS_PROPERTY) {
                        property.value = values[0];

                    }
                }

                return property;
            });

            updatePolicies([{ ...policies[0], properties }]);
        }

    }


    const isStepUpEnabled = useMemo(() => {
        return (policies || []).some(policy => policy.stepUpEnabled);
    }, [policies]);

    const trustScoreResources = resources.pages.verificationServiceDetails.tabs.policies.sections.details.sections.trustScore;

    return (
        <div>
            {!!selectedApiKey && !selectedStepUpApiKeyOption && (
                <Alert variant="danger">{trustScoreResources.invalidStepUpApiKey}</Alert>
            )}
            <ToggleFormGroup
                controlId={"policies-tab-trust-score-step-up-api-key-toggle"}
                automationId={"policies-tab-trust-score-step-up-api-key-toggle"}
                checked={isStepUpEnabled}
                disabled={isDisabled}
                isRequired={true}
                label={trustScoreResources.inputs.enableStepUp.label}
                ariaLabel={trustScoreResources.inputs.enableStepUp.label}
                onChange={handleEnableStepUpChange}
                value={`${isStepUpEnabled}`}
                checkedIcon={false}
                uncheckedIcon={false}
                labelWidth={LabelWidthTypes.MEDIUM}
            />
            {isStepUpEnabled && (
                <>
                    <SelectFormGroup
                        controlId={"policies-tab-trust-score-step-up-api-key-select-input"}
                        automationId={"policies-tab-trust-score-step-up-api-key-select-input"}
                        label={trustScoreResources.inputs.stepUpApiKey.label}
                        ariaLabel={trustScoreResources.inputs.stepUpApiKey.label}
                        disabled={isDisabled}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        layout={LayoutTypes.ROW}
                        labelWidth={LabelWidthTypes.MEDIUM}
                        onChange={handleStepUpApiKeyChange}
                        options={axnRpServiceOptions}
                        selectedOption={selectedStepUpApiKeyOption}
                    />
                    {shouldShowObligationWarningMessage && <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.policies.sections.details.obligationWarningMessage}</Alert>}
                    {validationErrors.noStepUpApiKeyError}
                </>
            )}

            {policies.length > 0 && <TrustScoreSliderControl
                controlId={`"policies-tab-trust-score-slider-control`}
                isDisabled={isDisabled}
                isStepUpEnabled={isStepUpEnabled}
                policyProperties={policies[0].properties}
                onValuesChanges={setProperties}
            />}
        </div>
    );
};